/**
 * PartyRelations — what a PARTY is, beyond its graph.
 *
 * The relationship lists themselves (works_at, the corporate group, and every
 * other registered edge) now come from `record/RecordRelations`, derived from
 * the metamodel for every entity (#484). This panel keeps only what that
 * derivation cannot express, and which is specific to a party:
 *
 *   · the commercial ROLES it wears — what "account" actually means (party_role);
 *   · its CONTACT POINTS — channels, not related records;
 *   · the PRINCIPAL that lets a person sign in (app_user);
 *   · the TIMELINE of activities pointing at it.
 *
 * It used to render the people/organizations lists too. Once the derived tabs
 * arrived that was the SAME edge shown twice on one page, in the same words —
 * so those blocks were removed rather than left to disagree. Per-edge facts,
 * including job title and optional validity dates, now belong to the generic
 * relationship grid (#495). Relationship-scoped contact channels remain here
 * only once the governed contact-point projection can supply them (#1075).
 */
import {
  Building2, Mail, Phone, Globe, MapPin, AtSign, IdCard, ArrowUpRight, Network,
  CalendarDays, StickyNote, CheckSquare, MessageCircle, MessageSquare, Clock, ArrowDownLeft, Plus,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import type { Row } from '@solum/sdk'
import { Button, Select, Surface, Pill, Spinner, type Tone } from '@solum/ui'
import { useLookupLabels, useRelated, useSaveEntity } from '../data/queries'
import { queryGate } from '../data/QueryBoundary'
import { me } from '../experience/governed'
import {
  availableOrganizationRoles, isRoleTypeAvailable, organizationRoleAssignment, roleTypeLabel,
} from './partyRoles'
import s from './PartyRelations.module.css'

const asRows = (d: unknown): Row[] => (Array.isArray(d) ? (d as Row[]) : [])
const toneOf = (t: unknown): Tone =>
  (['good', 'warn', 'critical', 'neutral', 'brand'].includes(String(t)) ? (String(t) as Tone) : 'neutral')

const KIND_ICON: Record<string, ReactNode> = {
  email: <Mail size={12} />, phone: <Phone size={12} />, website: <Globe size={12} />,
  address: <MapPin size={12} />, social: <AtSign size={12} />,
}

// ── Activity timeline (activities linked to this party by an edge) ──────────
const CHANNEL_ICON: Record<string, ReactNode> = {
  email: <Mail size={14} />, call: <Phone size={14} />, meeting: <CalendarDays size={14} />,
  note: <StickyNote size={14} />, task: <CheckSquare size={14} />,
  whatsapp: <MessageCircle size={14} />, sms: <MessageSquare size={14} />,
}
const STATUS_TONE: Record<string, Tone> = {
  draft: 'neutral', scheduled: 'warn', sent: 'good', completed: 'good', cancelled: 'critical',
}
const ACTIVITY_RELS = new Set(['activity_about', 'activity_with'])
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtDate = (iso?: unknown): string => {
  const v = typeof iso === 'string' ? iso : ''
  const [y, m, d] = v.split('-')
  return y && m && d ? `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}` : ''
}
const whenOf = (a: Row): string => String(a.occurred_at ?? a.due_at ?? '')
function ContactChip({ c }: { c: Row }) {
  return (
    <span className={s.chip}>
      <span className={s.chipIcon}>{KIND_ICON[String(c.kind)] ?? <Mail size={12} />}</span>
      <span>{String(c.value)}</span>
      {c.label ? <span className={s.chipLabel}>{String(c.label)}</span> : null}
    </span>
  )
}

export function PartyRelations({ entity, id }: { entity: string; id: string }) {
  const isPerson = entity === 'core_person'
  const [roleDraft, setRoleDraft] = useState('')

  // DELIBERATELY UNFILTERED, and this line is load-bearing (#1075). It feeds
  // BOTH the label/tone maps below and the picker. An archived role type must
  // still resolve its real label on every assignment that already carries it —
  // filtering here (`{ status: 'active' }`) is a one-line edit that silently
  // degrades a historical role to `titleCase(key)` with a neutral tone, which
  // reads as a value nobody recognises rather than as a retired one.
  // Availability is carried per option instead: `availableOrganizationRoles`
  // marks archived types `selectable: false` and the Select disables them.
  const roleTypes = useRelated('core', 'party_role_type', {}, !isPerson)
  const inEdges = useRelated('core', 'relationship', { to_id: id }) // people + activities point AT the party
  const contacts = useRelated('core', 'contact_point', {})
  const roles = useRelated('core', 'party_role', { party_id: id }, !isPerson)
  const principals = useRelated('core', 'app_user', { person_id: id }, isPerson)
  const saveRole = useSaveEntity('core_party_role')

  const toneMap = new Map(asRows(roleTypes.data).map((r) => [r.key, toneOf(r.tone)]))
  const allContacts = asRows(contacts.data)

  // The timeline: activities whose edge points at this party, newest first.
  // Read BY ID — exactly the activities the edges name, batched — rather than
  // the whole activity book filtered here (#2444 slice 4). An activity is a
  // subject entity: every row the door returns is a `record.viewed`, so a
  // 500-row book read to show three items would receipt 497 records nobody on
  // this page ever saw. `useLookupLabels` is `core.subject_get_many` for it.
  const timelineIds = [...new Set(asRows(inEdges.data).filter((e) => ACTIVITY_RELS.has(String(e.rel_type))).map((e) => String(e.from_id)))]
  const activities = useLookupLabels('activity_activity', timelineIds)
  const timeline = timelineIds
    .map((aid) => activities.byId.get(aid))
    .filter((a): a is Row => Boolean(a))
    .sort((x, y) => whenOf(y).localeCompare(whenOf(x)))

  // This panel's own comment says it "under-reported the graph it exists to make
  // visible" when a lookup pool was capped. A failed edge read does the same
  // thing more completely: every list below comes off `asRows(…data)`, so the
  // party rendered with no relationships, no contacts and an empty timeline —
  // which on a party page reads as "this account is unconnected" (#389).
  // Only the always-enabled queries are gated; `roleTypes`, `roles` and
  // `principals` are conditional on the party KIND, and a disabled TanStack query
  // reports `isLoading:false, data:undefined` — which `queryState` calls an error.
  if (inEdges.isLoading || contacts.isLoading) {
    return <Surface className={s.wrap}><div className={s.loading}><Spinner /></div></Surface>
  }
  const gate = queryGate([inEdges, contacts], 'this party’s detail')
  if (gate) return <Surface className={s.wrap}><div className={s.loading}>{gate}</div></Surface>

  const head = (icon: ReactNode, sub: string) => (
    <header className={s.head}>
      <div className={s.headIcon}>{icon}</div>
      <div>
        <h2 className={s.title}>Relationships</h2>
        <p className={s.sub}>{sub}</p>
      </div>
    </header>
  )
  // The timeline (activities ride the same graph) — shared by person + org.
  const timelineBlock = timeline.length > 0 ? (
    <div className={s.block}>
      <div className={s.label}>Timeline</div>
      <ol className={s.timeline}>
        {timeline.map((a) => {
          const isTask = a.channel === 'task'
          const when = fmtDate(isTask ? a.due_at : a.occurred_at)
          const inbound = a.direction === 'inbound'
          return (
            <li key={String(a.id)} className={s.tItem}>
              <div className={s.tGlyph}>{CHANNEL_ICON[String(a.channel)] ?? <Clock size={14} />}</div>
              <div className={s.cardMain}>
                <span className={s.tSubject}>{String(a.subject)}</span>
                <span className={s.line}>
                  {inbound ? <ArrowDownLeft size={12} /> : <ArrowUpRight size={12} />}
                  {String(a.channel)}{when ? ` · ${isTask ? 'Due ' : ''}${when}` : ''}
                </span>
              </div>
              <Pill tone={STATUS_TONE[String(a.status)] ?? 'neutral'}>{String(a.status)}</Pill>
            </li>
          )
        })}
      </ol>
    </div>
  ) : null

  // ── Person: the channels, the principal, the timeline ────────────────────
  // The organizations this person works at are a REGISTERED relationship and
  // are shown as a derived tab (#484). Repeating them here put the same edge on
  // the page twice, in the same words.
  if (isPerson) {
    const principalGate = queryGate([principals], 'this person’s sign-in identity')
    if (principalGate) return <Surface className={s.wrap}><div className={s.loading}>{principalGate}</div></Surface>
    const personal = allContacts.filter((c) => c.party_id === id && c.relationship_id == null)
    const principal = asRows(principals.data)[0]
    return (
      <Surface className={s.wrap} role="group" aria-label="Party detail">
        {head(<Network size={16} />, 'How to reach this person, and how they sign in.')}
        {personal.length > 0 && (
          <div className={s.block}>
            <div className={s.label}>Personal</div>
            <div className={s.chips}>{personal.map((c) => <ContactChip key={String(c.id)} c={c} />)}</div>
          </div>
        )}
        {principal && (
          <div className={s.block}>
            <div className={s.label}>Signs in</div>
            <div className={s.principal}>
              <IdCard size={15} />
              <span>Linked principal</span>
              <Pill tone="brand">{String(principal.kind)}</Pill>
            </div>
          </div>
        )}
        {timelineBlock}
      </Surface>
    )
  }

  // ── Organization: the roles it wears, its channels, its timeline ─────────
  // Its people and its corporate group are REGISTERED relationships and appear
  // as derived tabs (#484) — rendering them here as well was the same edge
  // twice on one page.
  const roleGate = queryGate([roleTypes, roles], 'this organization’s commercial roles')
  if (roleGate) return <Surface className={s.wrap}><div className={s.loading}>{roleGate}</div></Surface>
  const orgRoles = asRows(roles.data)
  const roleTypeRows = asRows(roleTypes.data)
  const roleTypeMap = new Map(roleTypeRows.map((r) => [String(r.key), r]))
  const roleOptions = availableOrganizationRoles(roleTypeRows, orgRoles)
  const orgContacts = allContacts.filter((c) => c.party_id === id && c.relationship_id == null)
  const saveRoleAndRefresh = async (role: string) => {
    await saveRole.mutateAsync(organizationRoleAssignment(orgRoles, id, role, me()))
    setRoleDraft('')
    await roles.refetch()
  }
  const setRoleStatus = async (role: Row, status: 'active' | 'inactive') => {
    await saveRole.mutateAsync({ id: String(role.id), patch: { status } })
    await roles.refetch()
  }
  return (
    <Surface className={s.wrap} role="group" aria-label="Party detail">
      {head(<Building2 size={16} />, 'The commercial roles this organization wears, and how to reach it.')}
      <div className={s.block}>
        <div className={s.label}>Roles</div>
        {orgRoles.length ? (
          <>
            <ul className={s.roleList}>
              {orgRoles.map((r) => {
                const type = roleTypeMap.get(String(r.role))
                const active = r.status === 'active'
                const label = roleTypeLabel(type, String(r.role))
                // The DEFINITION being archived and the ASSIGNMENT being
                // inactive are different facts and both are shown: a role can
                // be actively worn under a vocabulary entry the deployment has
                // since retired. The label is the real one either way.
                const archivedType = type !== undefined && !isRoleTypeAvailable(type)
                return (
                  <li key={String(r.id)} className={s.roleRow}>
                    <Pill tone={active ? (toneMap.get(r.role) ?? 'neutral') : 'neutral'}>{label}</Pill>
                    {archivedType && <span className={s.roleState} data-testid={`party-role-archived-${String(r.role)}`}>Archived</span>}
                    {!active && <span className={s.roleState}>Inactive</span>}
                    <Button size="sm" variant="ghost" loading={saveRole.isPending}
                      aria-label={`${active ? 'Deactivate' : 'Reactivate'} ${label} role`}
                      onClick={() => { void setRoleStatus(r, active ? 'inactive' : 'active').catch(() => undefined) }}>
                      {active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </li>
                )
              })}
            </ul>
            <p className={s.caption}>“Account” isn’t a table — it’s an organization wearing a commercial role.</p>
          </>
        ) : <div className={s.empty}>No roles assigned.</div>}
        {roleOptions.length > 0 && (
          <div className={s.roleAdd}>
            <Select value={roleDraft} onValueChange={setRoleDraft} options={roleOptions}
              placeholder="Choose a commercial role" testId="party-role-select" />
            <Button size="sm" icon={<Plus size={14} />} disabled={!roleDraft}
              loading={saveRole.isPending} data-testid="party-role-add"
              onClick={() => { if (roleDraft) void saveRoleAndRefresh(roleDraft).catch(() => undefined) }}>
              Assign role
            </Button>
          </div>
        )}
      </div>
      {orgContacts.length > 0 && (
        <div className={s.block}>
          <div className={s.label}>Contact</div>
          <div className={s.chips}>{orgContacts.map((c) => <ContactChip key={String(c.id)} c={c} />)}</div>
        </div>
      )}
      {timelineBlock}
    </Surface>
  )
}
